import _ from "lodash";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { profileLevelBearingsTableColumns, useQueryConfiguration } from "../../../Common/constants";
import { getAllBearingsByProfileId, updateBearingFromProfileBearingList } from "../../../api";
import { checkForAPISuccess, highlightSearchText, showErrorToast, showSuccessToast } from "../../../Common/commonFunctions";
import { Button, Card } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from 'primereact/column';
import { ClipLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

const ProfileBasedBearingList = () => {

    const profileId = window.location.pathname.split("/")[4];

    const [utils, setUtils] = useState({
        bearings: [],
        bearingsInitial: [],
        searchBearingNumber: "",
        bearingInEdit: {}
    })

    const returnSearchFilteredList = (searchedKey, bearingList) => {
        let updatedBearingsList = _.cloneDeep(bearingList);
        if(!_.isEmpty(searchedKey)) {
            updatedBearingsList = updatedBearingsList.filter(bearing => bearing?.bearingNumber.includes(searchedKey));
        }
        return updatedBearingsList;
    }

    const handleBearingNumberSearch = (searchedKey) => {
        setUtils({ ...utils, searchBearingNumber: searchedKey, bearings: returnSearchFilteredList(searchedKey, utils.bearingsInitial) });
    }

    const setBearingFieldOnEdit = (val, field) => setUtils({ ...utils, bearingInEdit: { ...utils.bearingInEdit, [`${field}`]: val } })

    const getColumn = (field, header, headerClass, isSortable) =>
        <Column
            sortable={isSortable}
            body={(row) => {
                if(
                    row?.bearingNumber === utils.bearingInEdit?.bearingNumber &&
                    field !== "bearingNumber"
                ) {
                    return (
                        header !== "rir" ?
                            <input
                                disabled={updateBearingMutation.isLoading}
                                onChange={(e) => setBearingFieldOnEdit(e.target.value, field)}
                                type="number"
                                className="bearing-list-input"
                                value={utils.bearingInEdit?.[field]?.toString() ? utils.bearingInEdit?.[field] : ""} 
                            />
                                :
                            <select
                                disabled={updateBearingMutation.isLoading}
                                onChange={(e) => setBearingFieldOnEdit(e.target.value === "true", field)}
                                value={utils.bearingInEdit?.[field]?.toString() ? utils.bearingInEdit?.[field] : "SELECT"}
                                className="bearing-list-input"
                            >
                                <option value={"SELECT"} disabled>Select</option>
                                <option>true</option>
                                <option>false</option>
                            </select>
                    );
                }

                if(
                    field === "bearingNumber" &&
                    row[field]?.includes(utils.searchBearingNumber)
                ) {
                    return (
                        <span
                            dangerouslySetInnerHTML={{ __html: highlightSearchText(row[field], utils.searchBearingNumber) }}
                        >
                        </span>
                    );
                }

                return <span>{row[field]?.toString()}</span>
            }}
            headerClassName={headerClass}
            field={field}
            header={header}
        />

    const updateBearing = async () => {
        const { data } = await updateBearingFromProfileBearingList(profileId, utils.bearingInEdit);
        return data;
    }

    const updateBearingMutation = useMutation(updateBearing);

    const getBearingByProfileId = async () => {
        const { data } = await getAllBearingsByProfileId(profileId);
        return data;
    }

    const getBearingByProfileIdQuery = useQuery("get-bearings-by-profile-id", getBearingByProfileId, { ...useQueryConfiguration });

    useEffect(() => {
        if(!_.isEmpty(profileId)) {
            getBearingByProfileIdQuery.refetch();
        }
    }, []);

    useEffect(() => {
        if(getBearingByProfileIdQuery.isFetched) {
            if(checkForAPISuccess(getBearingByProfileIdQuery?.data?.statusCode)) {
                setUtils({
                    ...utils,
                    bearings:getBearingByProfileIdQuery?.data?.data,
                    bearingsInitial:getBearingByProfileIdQuery?.data?.data 
                });
            }
            else {
                showErrorToast("Failed to fetch bearings.")
            }
            getBearingByProfileIdQuery.remove()
        }
        if(updateBearingMutation.isSuccess || updateBearingMutation.isError) {
            if(
                updateBearingMutation.isSuccess &&
                checkForAPISuccess(updateBearingMutation?.data?.statusCode)
            ) {
                let updatedBearingListInitial = _.cloneDeep(utils.bearingsInitial);
                updatedBearingListInitial.splice(
                    _.findIndex(updatedBearingListInitial, bearing => bearing?.bearingNumber === utils.bearingInEdit.bearingNumber),
                    1,
                    utils.bearingInEdit
                );
                let updatedBearingListCurrent = returnSearchFilteredList(utils.searchBearingNumber, updatedBearingListInitial);
                setUtils({
                    ...utils,
                    bearingInEdit: {},
                    bearingsInitial: updatedBearingListInitial,
                    bearings: updatedBearingListCurrent 
                });
                showSuccessToast("Updated successfully")
            }
            else {
                showErrorToast("Failed to update bearing");
            }
            updateBearingMutation.reset();
        }
    }, [getBearingByProfileIdQuery.isFetched, updateBearingMutation.isSuccess, updateBearingMutation.isError]);

    return (
        <div className="w-100 h-100">
            <Link to={{ pathname: `/profile/${profileId}` }}>
                <Button size="sm" className="mb-3 p-2 font-weight-bold">Go back to Profile</Button>
            </Link>
            <Card className="w-100 mx-1 h-100">
                <div className="py-2 justify-end w-100">
                    <input
                        value={utils.searchBearingNumber}
                        disabled={getBearingByProfileIdQuery.isFetching}
                        placeholder="Search by Bearing Number"
                        className="bearing-list-search-box"
                        onChange={(e) => handleBearingNumberSearch(e.target.value)}
                    />
                </div>
                <Card.Body className="my-0 p-0 mx-2 h-100">
                    <DataTable
                        emptyMessage={<b className="f-17">NO BEARING(S) FOUND</b>}
                        columnResizeMode="expand"
                        resizableColumns
                        loading={getBearingByProfileIdQuery.isFetching}
                        loadingIcon={<ClipLoader color="#075978" />}
                        cellClassName="data-table-cell"
                        rowClassName="data-table-row"
                        tableClassName="bearing-list-table"
                        paginator
                        rows={15}
                        paginatorClassName="my-2"
                        value={utils.bearings}
                    >
                        <Column
                            headerClassName="data-table-header"
                            header="Action"
                            body={(row) =>
                                <div className="data-table-action-column">
                                    {
                                        row?.bearingNumber !== utils.bearingInEdit?.bearingNumber ?
                                            <Icon
                                                onClick={() => setUtils({ ...utils, bearingInEdit: row })}
                                                icon={"feather-edit"}
                                                width={18}
                                                height={18}
                                                className={!_.isEmpty(utils.bearingInEdit?.bearingNumber) ? "disable-icon-bearing-list" : ""}
                                            />
                                                :
                                            <>
                                                {
                                                    updateBearingMutation.isLoading ?
                                                        <ClipLoader color="#075978" />
                                                            :
                                                        <>
                                                            <Icon
                                                                onClick={() => updateBearingMutation.mutate()}
                                                                icon={"feather-check"}
                                                                width={18}
                                                                height={18}
                                                            />
                                                            <Icon
                                                                onClick={() => setUtils({ ...utils, bearingInEdit: {} })}
                                                                icon={"feather-x"}
                                                                width={18}
                                                                height={18}
                                                            />
                                                        </>
                                                }
                                            </>
                                    }
                                </div>
                            }
                        />
                        {
                            Object.entries(profileLevelBearingsTableColumns).map((entry) => 
                                getColumn(entry[0], entry[1], "data-table-header")
                            )
                        }
                    </DataTable>
                </Card.Body>
            </Card>
        </div>
    )
}

export default ProfileBasedBearingList;